// Theme presets — each overrides app-level CSS variables for instant restyling.
// Variables: --client (primary), --coral (accent 1), --sun (accent 2), --background, --foreground

export const THEME_PRESETS = [
  {
    id: "teal",
    name: "Calm Teal",
    description: "The default — warm cream + teal + coral.",
    swatch: ["#2A9D8F", "#E76F51", "#E9C46A", "#FDFBF7"],
    vars: {},  // default — empty
  },
  {
    id: "sunshine",
    name: "Sunshine",
    description: "Sunny golden yellows and sky blue.",
    swatch: ["#F4A261", "#E76F51", "#90C2E7", "#FFFAF0"],
    vars: {
      "--client": "30 96% 56%",
      "--coral": "13 75% 62%",
      "--sun": "203 60% 73%",
      "--accent": "13 75% 62%",
      "--background": "39 100% 97%",
      "--ring": "30 96% 56%",
    },
  },
  {
    id: "ocean",
    name: "Ocean Breeze",
    description: "Deep blues with coral and seafoam.",
    swatch: ["#1E6091", "#48CAE4", "#F77F00", "#F0F8FF"],
    vars: {
      "--client": "203 67% 35%",
      "--coral": "27 96% 49%",
      "--sun": "192 76% 59%",
      "--accent": "192 76% 59%",
      "--background": "208 100% 97%",
      "--ring": "203 67% 35%",
      "--foreground": "215 30% 18%",
    },
  },
  {
    id: "forest",
    name: "Forest Calm",
    description: "Earthy greens, cream, terracotta.",
    swatch: ["#5A8A4A", "#C97B53", "#E8D8A6", "#FAF6EE"],
    vars: {
      "--client": "104 30% 42%",
      "--coral": "20 50% 56%",
      "--sun": "44 64% 78%",
      "--accent": "20 50% 56%",
      "--background": "44 50% 96%",
      "--ring": "104 30% 42%",
    },
  },
  {
    id: "lavender",
    name: "Lavender Dream",
    description: "Soft purples and pinks for a calming feel.",
    swatch: ["#7B5EA7", "#EE82A2", "#FFD580", "#F8F5FB"],
    vars: {
      "--client": "267 30% 51%",
      "--coral": "338 78% 72%",
      "--sun": "37 100% 75%",
      "--accent": "338 78% 72%",
      "--background": "270 40% 98%",
      "--ring": "267 30% 51%",
    },
  },
  {
    id: "sunset",
    name: "Sunset Berry",
    description: "Bold pinks, magenta, and golden orange.",
    swatch: ["#D72660", "#F46036", "#F9C846", "#FFF5F0"],
    vars: {
      "--client": "338 70% 50%",
      "--coral": "16 90% 58%",
      "--sun": "44 94% 63%",
      "--accent": "16 90% 58%",
      "--background": "20 100% 98%",
      "--ring": "338 70% 50%",
    },
  },
  {
    id: "midnight",
    name: "Midnight Pop",
    description: "Deep navy with electric cyan & magenta.",
    swatch: ["#22D3EE", "#F472B6", "#FBBF24", "#0F172A"],
    vars: {
      "--client": "188 86% 53%",
      "--coral": "330 81% 70%",
      "--sun": "43 96% 56%",
      "--accent": "330 81% 70%",
      "--background": "222 47% 11%",
      "--foreground": "210 40% 98%",
      "--card": "222 35% 15%",
      "--card-foreground": "210 40% 98%",
      "--popover": "222 35% 15%",
      "--popover-foreground": "210 40% 98%",
      "--muted": "222 32% 19%",
      "--muted-foreground": "215 20% 70%",
      "--border": "222 32% 22%",
      "--input": "222 32% 22%",
      "--ring": "188 86% 53%",
      "--secondary": "222 32% 19%",
      "--secondary-foreground": "210 40% 98%",
    },
  },
];

export function applyThemePreset(presetId) {
  const root = document.documentElement;
  // Reset previous custom vars
  ["--client", "--coral", "--sun", "--accent", "--background", "--foreground",
   "--card", "--card-foreground", "--popover", "--popover-foreground",
   "--muted", "--muted-foreground", "--border", "--input", "--ring",
   "--secondary", "--secondary-foreground"].forEach((v) => root.style.removeProperty(v));
  const preset = THEME_PRESETS.find((p) => p.id === presetId);
  if (!preset || !preset.vars) return;
  Object.entries(preset.vars).forEach(([k, v]) => root.style.setProperty(k, v));
}

// AI photo styles — preset prompts the user can apply with one tap
export const AI_PHOTO_STYLES = [
  { id: "cartoon", label: "Cartoon", emoji: "🎨", prompt: "Convert this photo into a friendly hand-drawn cartoon illustration with warm pastel colours, soft shading, and a cheerful expression. Keep the person clearly recognisable." },
  { id: "watercolor", label: "Watercolor", emoji: "🖌️", prompt: "Transform this photo into a soft watercolor painting with gentle brushstrokes and a creamy paper background. Maintain the person's likeness." },
  { id: "popart", label: "Pop Art", emoji: "✨", prompt: "Restyle this photo as bold pop-art with thick outlines, halftone dots, and vibrant 4-colour palette (red, yellow, blue, white). Keep the person recognisable." },
  { id: "pencil", label: "Pencil Sketch", emoji: "✏️", prompt: "Turn this photo into a detailed pencil sketch with cross-hatching and soft graphite shading on white paper. Preserve facial features." },
  { id: "vintage", label: "Vintage", emoji: "📷", prompt: "Make this photo look like a vintage 1970s portrait — warm sepia tones, gentle film grain, slight vignette. Keep the person recognisable." },
  { id: "professional", label: "Professional", emoji: "💼", prompt: "Enhance this photo for a professional headshot — clean background, balanced lighting, slight smile, polished but natural look. Keep the person clearly recognisable." },
  { id: "anime", label: "Anime", emoji: "🌸", prompt: "Transform this photo into a friendly anime-style illustration with soft cel-shading, expressive eyes, and warm colours. Keep the person recognisable." },
  { id: "comic", label: "Comic Hero", emoji: "💥", prompt: "Restyle this photo as a comic book hero — bold inked outlines, dramatic lighting, dynamic colour. Keep the person clearly recognisable." },
];
